/**
 * South African ID number validation — the client-side mirror of the
 * backend's users/validators.py, so the apply form can flag a typo before
 * the round trip. The backend remains the authority; keep the rules in sync.
 *
 * Format: 13 digits, YYMMDD SSSS C A Z
 *   YYMMDD  date of birth (century not encoded; 19YY and 20YY both legal)
 *   SSSS    gender sequence — not validated
 *   C       citizenship: 0 = citizen, 1 = permanent resident
 *   A       historical digit — not validated
 *   Z       Luhn check digit
 */

function luhnChecksumOk(digits) {
  let total = 0;
  for (let i = 0; i < digits.length; i += 1) {
    let d = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    total += d;
  }
  return total % 10 === 0;
}

function birthDigitsAreARealDate(digits) {
  const yy = Number(digits.slice(0, 2));
  const mm = Number(digits.slice(2, 4));
  const dd = Number(digits.slice(4, 6));
  // Both centuries must be tried: 000229 is 29 Feb 2000 (a leap day) even
  // though 1900 was not a leap year.
  return [1900, 2000].some((century) => {
    const d = new Date(century + yy, mm - 1, dd);
    return (
      d.getFullYear() === century + yy &&
      d.getMonth() === mm - 1 &&
      d.getDate() === dd
    );
  });
}

/**
 * Validate an SA ID number. Returns null when valid, or a message the person
 * filling in the form can act on.
 */
export function saIdError(value) {
  const id = (value || '').trim();
  if (!/^\d{13}$/.test(id)) {
    return 'SA ID number must be exactly 13 digits.';
  }
  if (!birthDigitsAreARealDate(id.slice(0, 6))) {
    return 'The first six digits must be your date of birth (YYMMDD).';
  }
  if (id[10] !== '0' && id[10] !== '1') {
    return 'The 11th digit must be 0 (citizen) or 1 (permanent resident).';
  }
  if (!luhnChecksumOk(id)) {
    return 'This ID number fails its checksum — please re-check the digits.';
  }
  return null;
}

export function isValidSaId(value) {
  return saIdError(value) === null;
}

/**
 * The calendar date three months before `day` (clamped to month length),
 * as a Date. FICA's proof-of-address freshness bound.
 */
export function threeMonthsBefore(day) {
  const d = new Date(day.getFullYear(), day.getMonth() - 3, 1);
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day.getDate(), lastDay));
  return d;
}

/**
 * Validate the proof-of-address issue date (an <input type="date"> value,
 * "YYYY-MM-DD"). Returns null when acceptable, else an actionable message.
 */
export function proofOfAddressDateError(value) {
  if (!value) return 'Please enter the issue date shown on the document.';
  const [y, m, d] = value.split('-').map(Number);
  const issued = new Date(y, m - 1, d);
  if (Number.isNaN(issued.getTime())) return 'Please enter a valid date.';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (issued > today) return 'The issue date cannot be in the future.';
  if (issued < threeMonthsBefore(today)) {
    return 'Proof of address must be dated within the last 3 months — please upload a more recent document.';
  }
  return null;
}
